import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

/** Pick the best translation from a JSONB field; falls back to English then any value. */
export function pickLocalized(field: any, lang: string): string {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    return field[lang] || field.en || Object.values(field)[0] as string || '';
  }
  return String(field);
}

export function useLocalize() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  return (field: any) => pickLocalized(field, lang);
}

export function useHero() {
  return useQuery({
    queryKey: ['landing-hero'],
    queryFn: async () => {
      const { data } = await supabase
        .from('landing_hero')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });
}

export function useCities() {
  return useQuery({
    queryKey: ['cities-active'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cities')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useExploreTiles() {
  return useQuery({
    queryKey: ['explore-tiles'],
    queryFn: async () => {
      const { data } = await supabase
        .from('explore_tiles')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useLatestNews(limit = 3) {
  return useQuery({
    queryKey: ['latest-news', limit],
    queryFn: async () => {
      const { data } = await supabase
        .from('news_articles')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(limit);
      return data || [];
    },
  });
}

export function useLatestBlog(limit = 3) {
  return useQuery({
    queryKey: ['latest-blog', limit],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(limit);
      return data || [];
    },
  });
}

export function usePartners() {
  return useQuery({
    queryKey: ['landing-partners'],
    queryFn: async () => {
      const { data } = await supabase
        .from('landing_partners')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePartnerStats() {
  return useQuery({
    queryKey: ['landing-partner-stats'],
    queryFn: async () => {
      const { data } = await supabase
        .from('landing_partner_stats')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}
